import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircleQuestionIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  Field,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  TextArea,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';

/** A client's concierge question, as the staff endpoint lists them. */
export interface ConciergeQuestionRow {
  readonly id: string;
  readonly topic: string;
  readonly subject: string;
  readonly body: string;
  readonly status: 'OPEN' | 'IN_PROGRESS' | 'ANSWERED' | 'CLOSED';
  readonly response: string | null;
  readonly respondedAt: string | null;
  readonly respondedBy: string | null;
  readonly createdAt: string;
  readonly requester: { readonly name: string; readonly email: string } | null;
}

/** Longest answer the API takes. */
const ANSWER_MAX_LENGTH = 4000;

const STATUS_LABELS: Readonly<Record<ConciergeQuestionRow['status'], string>> = {
  OPEN: 'Waiting for an answer',
  IN_PROGRESS: 'Waiting for an answer',
  ANSWERED: 'Answered',
  CLOSED: 'Closed',
};

const SORT_FIELDS = {
  question: 'subject',
  askedBy: 'askedBy',
  asked: 'createdAt',
  status: 'statusLabel',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Question', sortKey: SORT_FIELDS.question },
  { label: 'Asked by', sortKey: SORT_FIELDS.askedBy },
  { label: 'Asked', sortKey: SORT_FIELDS.asked },
  { label: 'Status', sortKey: SORT_FIELDS.status },
];
/** Newest first, so a new question is at the top. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.asked, direction: 'desc' };

function readableTopic(topic: string): string {
  const words = topic.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function readableDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * A client's concierge questions, and the place BCE answers them (2026-09-27).
 * The answer reaches the client on their Help page, with an alert (and an
 * email when they have that turned on). The concierge email BCE receives
 * links here.
 *
 * @param props - The client.
 * @returns The Questions tab.
 */
export function OrgQuestionsTab({ organizationId }: { readonly organizationId: string }): React.ReactElement {
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);

  const questions = useQuery({
    queryKey: ['org-concierge', organizationId],
    queryFn: ({ signal }) =>
      getJson<ConciergeQuestionRow[]>(`/organizations/${organizationId}/concierge`, undefined, signal),
  });

  const rows = (questions.data ?? []).map((row) => ({
    ...row,
    askedBy: row.requester?.name ?? '',
    statusLabel: STATUS_LABELS[row.status],
  }));
  const needle = search.trim().toLowerCase();
  const shown = sortRows(
    needle === '' ? rows : rows.filter((row) => `${row.subject} ${row.body} ${row.askedBy} ${row.topic}`.toLowerCase().includes(needle)),
    sort ?? DEFAULT_SORT,
  );
  const open = rows.find((row) => row.id === openId) ?? null;
  const firstWaitingId = shown.find((row) => row.status === 'OPEN' || row.status === 'IN_PROGRESS')?.id;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-portal-section">Concierge questions</h2>
          <p className="mt-1 text-portal-small text-portal-muted">
            What this client asked from their Help page. Answer here and they see it in the portal.
          </p>
        </div>
        <SearchInput
          id="concierge-search"
          label="Search concierge questions"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search questions…"
        />
      </div>

      <div className="mt-4">
        {questions.isError ? (
          <ErrorNotice title="Couldn’t load questions" message={errorMessage(questions.error)} />
        ) : questions.isPending ? (
          <SkeletonTable rows={3} columns={['w-[45%]', 'w-[25%]']} label="Loading questions…" />
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<MessageCircleQuestionIcon aria-hidden className="size-6" />}
              title="No questions yet"
              message="When someone at this client asks the concierge from their Help page, it appears here to answer."
            />
          </Panel>
        ) : (
          <DataTable guide="concierge-table" headers={HEADERS} {...(sort === undefined ? {} : { sort })} onSort={onSort}>
            {shown.map((row) => (
              <tr key={row.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    // The first question still waiting is the one the tour answers.
                    data-guide={row.id === firstWaitingId ? 'concierge-open-waiting' : undefined}
                    onClick={() => setOpenId(row.id)}
                    className="text-left font-bold text-brand underline-offset-2 hover:underline"
                  >
                    {row.subject}
                  </button>
                  <span className="block text-portal-micro text-portal-muted">{readableTopic(row.topic)}</span>
                </td>
                <td className="p-3 text-portal-small">
                  {row.requester?.name ?? 'Unknown member'}
                  {row.requester ? (
                    <span className="block text-portal-micro text-portal-muted">{row.requester.email}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDate(row.createdAt)}</td>
                <td className="p-3 text-portal-small">
                  {row.status === 'ANSWERED' || row.status === 'CLOSED' ? (
                    <span className="text-portal-muted">{row.statusLabel}</span>
                  ) : (
                    <Pill tone="warning">{row.statusLabel}</Pill>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      <QuestionDrawer organizationId={organizationId} question={open} onClose={() => setOpenId(null)} />
    </div>
  );
}

/**
 * One question: what was asked, and either BCE's answer or the box to write it.
 *
 * @param props - The question (null when closed).
 * @returns The side panel.
 */
function QuestionDrawer({
  organizationId,
  question,
  onClose,
}: {
  readonly organizationId: string;
  readonly question: ConciergeQuestionRow | null;
  readonly onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState('');
  const send = useMutation({
    mutationFn: (input: { id: string; response: string }) =>
      sendJson('post', `/organizations/${organizationId}/concierge/${input.id}/answer`, { response: input.response }),
    onSuccess: async () => {
      setAnswer('');
      await queryClient.invalidateQueries({ queryKey: ['org-concierge', organizationId] });
    },
  });
  const answered = question?.response ?? null;

  return (
    <Drawer
      open={question !== null}
      title={question?.subject ?? 'Question'}
      {...(question
        ? { subtitle: `${question.requester?.name ?? 'Unknown member'} · ${readableDate(question.createdAt)} · ${readableTopic(question.topic)}` }
        : {})}
      onClose={() => {
        if (!send.isPending) onClose();
      }}
      {...(question && answered === null
        ? {
            footer: (
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  guide="concierge-send"
                  disabled={answer.trim().length === 0 || send.isPending}
                  onClick={() => send.mutate({ id: question.id, response: answer.trim() })}
                >
                  {send.isPending ? 'Sending…' : 'Send answer'}
                </Button>
              </div>
            ),
          }
        : {})}
    >
      {question === null ? null : (
        <div className="space-y-5">
          <p data-guide="concierge-question" className="rounded-portal-button bg-portal-bg p-3.5 text-portal-small leading-[1.6] whitespace-pre-line">
            {question.body}
          </p>

          {answered !== null ? (
            <div data-guide="concierge-answered">
              <p className="text-portal-micro font-bold text-portal-muted">
                Answered by {question.respondedBy ?? 'BCE'}
                {question.respondedAt ? ` on ${readableDate(question.respondedAt)}` : ''}
              </p>
              <p className="mt-1 text-portal-small leading-[1.6] whitespace-pre-line">{answered}</p>
            </div>
          ) : (
            <Field
              guide="concierge-answer"
              label="Your answer"
              htmlFor="concierge-answer"
              required
              hint="The client reads this on their Help page and gets an alert pointing there."
            >
              <TextArea
                id="concierge-answer"
                rows={8}
                maxLength={ANSWER_MAX_LENGTH}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
              />
            </Field>
          )}

          {send.isError ? <ErrorNotice inline title="Answer not sent" message={errorMessage(send.error)} /> : null}
        </div>
      )}
    </Drawer>
  );
}
